<template>
  <div class="knowledge-chat-fab-root">
    <transition name="fab-backdrop">
      <div
        v-if="isOpen"
        class="fab-backdrop"
        :class="{ 'backdrop-desktop': isExpanded }"
        @click="closePanel"
        aria-hidden="true"
      />
    </transition>

    <transition name="fab-panel">
      <div
        v-if="isOpen"
        :class="['fab-panel', { fullscreen: isExpanded }]"
        role="dialog"
        aria-label="Renox Assistant"
      >
        <KnowledgeChatPanel
          variant="widget"
          :expandable="true"
          :is-expanded="isExpanded"
          @toggle-expand="toggleExpand"
        />
      </div>
    </transition>

    <button
      type="button"
      :class="['fab-button', { open: isOpen }]"
      @click="togglePanel"
      :aria-expanded="isOpen"
      aria-label="Open knowledge assistant"
    >
      <font-awesome-icon :icon="['fas', isOpen ? 'times' : 'comments']" />
    </button>
  </div>
</template>

<script>
import KnowledgeChatPanel from './KnowledgeChatPanel.vue';

export default {
  name: 'KnowledgeChatFab',
  components: {
    KnowledgeChatPanel,
  },
  data() {
    return {
      isOpen: false,
      isExpanded: false,
    };
  },
  mounted() {
    document.addEventListener('keydown', this.handleEscape);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscape);
  },
  methods: {
    togglePanel() {
      if (this.isOpen) {
        this.closePanel();
      } else {
        this.isOpen = true;
      }
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    closePanel() {
      this.isOpen = false;
      this.isExpanded = false;
    },
    handleEscape(event) {
      if (event.key !== 'Escape' || !this.isOpen) {
        return;
      }
      if (this.isExpanded) {
        this.isExpanded = false;
        return;
      }
      this.closePanel();
    },
  },
};
</script>

<style scoped>
.knowledge-chat-fab-root {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 1000;
}

.fab-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 999;
  display: block;
}

@media (min-width: 601px) {
  .fab-backdrop {
    display: none;
  }

  .fab-backdrop.backdrop-desktop {
    display: block;
  }
}

.fab-panel {
  position: fixed;
  right: 1.25rem;
  bottom: 5.25rem;
  width: 380px;
  height: 520px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transition: width 0.28s ease, height 0.28s ease, inset 0.28s ease, transform 0.28s ease, border-radius 0.28s ease;
}

.fab-panel :deep(.knowledge-chat-panel) {
  height: 100%;
}

.fab-panel.fullscreen {
  inset: 2.5rem 1rem 1rem;
  right: auto;
  left: 50%;
  transform: translateX(-50%);
  width: min(900px, 92vw);
  height: min(85vh, calc(100dvh - 3.5rem));
  border-radius: 1rem;
  overflow: hidden;
}

.fab-button {
  width: 3.5rem;
  height: 3.5rem;
  border: none;
  border-radius: 50%;
  background: #059669;
  color: #ffffff;
  font-size: 1.2rem;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(5, 150, 105, 0.4);
  transition: transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease;
  animation: fabPulse 2.5s ease-in-out infinite;
}

.fab-button:hover {
  transform: scale(1.06);
  background: #047857;
  box-shadow: 0 10px 28px rgba(5, 150, 105, 0.5);
}

.fab-button.open {
  animation: none;
  background: #374151;
}

.fab-button.open:hover {
  background: #1f2937;
}

.fab-panel-enter-active,
.fab-panel-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.fab-panel-enter-from,
.fab-panel-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
}

.fab-panel.fullscreen.fab-panel-enter-from,
.fab-panel.fullscreen.fab-panel-leave-to {
  transform: translateX(-50%) translateY(16px) scale(0.98);
}

.fab-backdrop-enter-active,
.fab-backdrop-leave-active {
  transition: opacity 0.28s ease;
}

.fab-backdrop-enter-from,
.fab-backdrop-leave-to {
  opacity: 0;
}

@keyframes fabPulse {
  0%, 100% {
    box-shadow: 0 8px 24px rgba(5, 150, 105, 0.4);
  }
  50% {
    box-shadow: 0 8px 32px rgba(5, 150, 105, 0.55);
  }
}

@media (max-width: 600px) {
  .knowledge-chat-fab-root {
    right: 0;
    bottom: 0;
    left: 0;
  }

  .fab-panel {
    right: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    height: min(85vh, 600px);
    border-radius: 1rem 1rem 0 0;
    overflow: hidden;
    transform: none;
  }

  .fab-panel.fullscreen {
    inset: 0;
    width: 100%;
    height: 100dvh;
    transform: none;
    left: 0;
    border-radius: 0;
  }

  .fab-button {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
  }
}
</style>
